/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/solid'
import {
    buildDateRangeFromPreset,
    buildMonthCalendarDays,
    DATE_RANGE_PRESET_ORDER,
    DateRangePreset,
    isDateInInclusiveRange,
    resolveDateRangePreset,
} from 'utils/dateRangePresets'

export type UiDateRangeCalendarValue = {
    from: Dayjs | null
    to: Dayjs | null
}

interface UiDateRangeCalendarProps {
    value: UiDateRangeCalendarValue | null
    onChange: (value: UiDateRangeCalendarValue | null) => void
    className?: string
    disabled?: boolean
}

/** Monday-first short weekday labels (i18n keys already mapped DE: Mo Di Mi…). */
const WEEKDAY_KEYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const

const presetLabelKey = (preset: DateRangePreset): string => {
    switch (preset) {
        case DateRangePreset.OneDay:
            return '1 Day'
        case DateRangePreset.OneWeek:
            return '1 Week'
        case DateRangePreset.TwoWeeks:
            return '2 Weeks'
        case DateRangePreset.OneMonth:
            return '1 Month'
        case DateRangePreset.Custom:
            return 'Custom'
        default:
            return 'Custom'
    }
}

const resolveInitialMonths = (
    from: Dayjs | null,
    to: Dayjs | null
): { left: Dayjs; right: Dayjs } => {
    const left = (from ?? dayjs()).startOf('month')
    const toMonth = to?.startOf('month') ?? null
    const right =
        toMonth && toMonth.isAfter(left, 'month') ? toMonth : left.add(1, 'month')
    return { left, right }
}

interface MonthPanelProps {
    month: Dayjs
    disabled?: boolean
    displayFrom: Dayjs | null
    displayTo: Dayjs | null
    onDayClick: (day: Dayjs) => void
    onShiftMonth: (delta: number) => void
    onShiftYear: (delta: number) => void
    canGoBackMonth: boolean
    canGoBackYear: boolean
    canGoForwardMonth: boolean
    canGoForwardYear: boolean
}

const MonthPanel: React.FC<MonthPanelProps> = ({
    month,
    disabled,
    displayFrom,
    displayTo,
    onDayClick,
    onShiftMonth,
    onShiftYear,
    canGoBackMonth,
    canGoBackYear,
    canGoForwardMonth,
    canGoForwardYear,
}) => {
    const { t } = useTranslation()
    const days = useMemo(() => buildMonthCalendarDays(month), [month])

    return (
        <div className="min-w-[252px] flex-1">
            <div className="mb-2 flex items-center justify-between gap-1 px-0.5">
                <div className="flex items-center gap-0.5">
                    <button
                        type="button"
                        disabled={disabled || !canGoBackYear}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                        onClick={() => onShiftYear(-1)}
                        aria-label={t('Previous year') as string}
                    >
                        <ChevronDoubleLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        disabled={disabled || !canGoBackMonth}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                        onClick={() => onShiftMonth(-1)}
                        aria-label={t('Previous month') as string}
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                </div>
                <p className="text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                    {month.format('MMM YYYY')}
                </p>
                <div className="flex items-center gap-0.5">
                    <button
                        type="button"
                        disabled={disabled || !canGoForwardMonth}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                        onClick={() => onShiftMonth(1)}
                        aria-label={t('Next month') as string}
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        disabled={disabled || !canGoForwardYear}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                        onClick={() => onShiftYear(1)}
                        aria-label={t('Next year') as string}
                    >
                        <ChevronDoubleRightIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 text-center text-[11px] font-medium text-gray-400 dark:text-gray-500">
                {WEEKDAY_KEYS.map((key) => (
                    <div key={key} className="py-1">
                        {t(key)}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7">
                {days.map((day) => {
                    const inMonth = day.isSame(month, 'month')
                    const isStart = Boolean(displayFrom && day.isSame(displayFrom, 'day'))
                    const isEnd = Boolean(displayTo && day.isSame(displayTo, 'day'))
                    const inRange =
                        displayFrom && displayTo
                            ? isDateInInclusiveRange(day, displayFrom, displayTo)
                            : Boolean(displayFrom && day.isSame(displayFrom, 'day'))
                    const isToday = day.isSame(dayjs(), 'day')
                    const isEndpoint = isStart || isEnd

                    if (!inMonth) {
                        return <div key={day.format('YYYY-MM-DD')} className="h-9" />
                    }

                    return (
                        <button
                            key={day.format('YYYY-MM-DD')}
                            type="button"
                            disabled={disabled}
                            aria-label={day.format('YYYY-MM-DD')}
                            onClick={() => onDayClick(day)}
                            className={clsx(
                                'relative flex h-9 items-center justify-center',
                                !disabled && 'cursor-pointer'
                            )}
                        >
                            {inRange ? (
                                <span
                                    className={clsx(
                                        'absolute inset-y-1 bg-cta-50 dark:bg-cta-900/35',
                                        isStart && isEnd && 'inset-x-1 rounded-full',
                                        isStart && !isEnd && 'left-1/2 right-0',
                                        isEnd && !isStart && 'left-0 right-1/2',
                                        !isStart && !isEnd && 'inset-x-0'
                                    )}
                                />
                            ) : null}

                            <span
                                className={clsx(
                                    'relative z-10 flex h-7 w-7 items-center justify-center rounded-full text-xs transition-colors',
                                    !inRange && 'text-gray-800 dark:text-gray-100',
                                    inRange && !isEndpoint && 'text-gray-800 dark:text-gray-100',
                                    isEndpoint && 'bg-cta-light font-semibold text-white',
                                    !disabled &&
                                        !isEndpoint &&
                                        'hover:bg-cta-50 dark:hover:bg-cta-900/30'
                                )}
                            >
                                {day.date()}
                                {isToday && !isEndpoint ? (
                                    <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-cta-light" />
                                ) : null}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const UiDateRangeCalendar: React.FC<UiDateRangeCalendarProps> = ({
    value,
    onChange,
    className,
    disabled,
}) => {
    const { t } = useTranslation()
    const from = value?.from ?? null
    const to = value?.to ?? null

    const initial = resolveInitialMonths(from, to)
    const [leftMonth, setLeftMonth] = useState<Dayjs>(initial.left)
    const [rightMonth, setRightMonth] = useState<Dayjs>(initial.right)
    const [draftStart, setDraftStart] = useState<Dayjs | null>(null)
    const [activePreset, setActivePreset] = useState<DateRangePreset>(() =>
        resolveDateRangePreset(from, to)
    )

    useEffect(() => {
        const next = resolveInitialMonths(from, to)
        setLeftMonth(next.left)
        setRightMonth(next.right)
        setActivePreset(resolveDateRangePreset(from, to))
        setDraftStart(null)
    }, [from, to])

    const applyRange = (nextFrom: Dayjs, nextTo: Dayjs, preset: DateRangePreset) => {
        const ordered = nextTo.isBefore(nextFrom, 'day')
            ? { from: nextTo.startOf('day'), to: nextFrom.startOf('day') }
            : { from: nextFrom.startOf('day'), to: nextTo.startOf('day') }
        setActivePreset(preset)
        setDraftStart(null)
        onChange(ordered)
    }

    const handlePresetClick = (preset: DateRangePreset) => {
        if (disabled) {
            return
        }
        if (preset === DateRangePreset.Custom) {
            setActivePreset(DateRangePreset.Custom)
            return
        }
        const anchor = from ?? dayjs()
        const range = buildDateRangeFromPreset(preset, anchor)
        if (!range) {
            return
        }
        const months = resolveInitialMonths(range.from, range.to)
        setLeftMonth(months.left)
        setRightMonth(months.right)
        applyRange(range.from, range.to, preset)
    }

    const handleDayClick = (day: Dayjs) => {
        if (disabled) {
            return
        }

        if (!draftStart) {
            setDraftStart(day.startOf('day'))
            setActivePreset(DateRangePreset.Custom)
            return
        }

        applyRange(draftStart, day, DateRangePreset.Custom)
    }

    /**
     * Independent navigation: each panel moves on its own.
     * Soft guard only — panels must stay ordered (left strictly before right)
     * so July | December is allowed, but overlapping the same month is not.
     */
    const shiftLeft = (unit: 'month' | 'year', delta: number) => {
        setLeftMonth((current) => {
            const next = current.add(delta, unit).startOf('month')
            return next.isBefore(rightMonth, 'month') ? next : current
        })
    }

    const shiftRight = (unit: 'month' | 'year', delta: number) => {
        setRightMonth((current) => {
            const next = current.add(delta, unit).startOf('month')
            return next.isAfter(leftMonth, 'month') ? next : current
        })
    }

    const leftCanGoForwardMonth = leftMonth.add(1, 'month').isBefore(rightMonth, 'month')
    const leftCanGoForwardYear = leftMonth.add(1, 'year').isBefore(rightMonth, 'month')
    const rightCanGoBackMonth = rightMonth.subtract(1, 'month').isAfter(leftMonth, 'month')
    const rightCanGoBackYear = rightMonth.subtract(1, 'year').isAfter(leftMonth, 'month')

    const displayFrom = draftStart ?? from
    const displayTo = draftStart ? null : to

    return (
        <div
            className={clsx(
                'UiDateRangeCalendar flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3.5 shadow-xl dark:border-gray-700 dark:bg-gray-900',
                className
            )}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                    <MonthPanel
                        month={leftMonth}
                        disabled={disabled}
                        displayFrom={displayFrom}
                        displayTo={displayTo}
                        onDayClick={handleDayClick}
                        onShiftMonth={(delta) => shiftLeft('month', delta)}
                        onShiftYear={(delta) => shiftLeft('year', delta)}
                        canGoBackMonth
                        canGoBackYear
                        canGoForwardMonth={leftCanGoForwardMonth}
                        canGoForwardYear={leftCanGoForwardYear}
                    />
                    <MonthPanel
                        month={rightMonth}
                        disabled={disabled}
                        displayFrom={displayFrom}
                        displayTo={displayTo}
                        onDayClick={handleDayClick}
                        onShiftMonth={(delta) => shiftRight('month', delta)}
                        onShiftYear={(delta) => shiftRight('year', delta)}
                        canGoBackMonth={rightCanGoBackMonth}
                        canGoBackYear={rightCanGoBackYear}
                        canGoForwardMonth
                        canGoForwardYear
                    />
                </div>

                <div className="flex w-full shrink-0 flex-row flex-wrap gap-1.5 sm:w-[7.25rem] sm:flex-col sm:self-stretch sm:justify-between">
                    {DATE_RANGE_PRESET_ORDER.map((preset) => {
                        const selected = activePreset === preset
                        return (
                            <button
                                key={preset}
                                type="button"
                                disabled={disabled}
                                onClick={() => handlePresetClick(preset)}
                                className={clsx(
                                    'w-full rounded-lg px-2 py-1.5 text-center text-[11px] leading-tight font-medium transition-colors sm:flex-1 sm:flex sm:items-center sm:justify-center',
                                    selected
                                        ? 'bg-cta-light text-white'
                                        : 'bg-gray-100 text-cta-light hover:bg-cta-50 dark:bg-gray-800 dark:text-cta-light dark:hover:bg-cta-900/30',
                                    disabled && 'cursor-not-allowed opacity-50'
                                )}
                            >
                                {t(presetLabelKey(preset))}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default UiDateRangeCalendar
